// Copyright 2016 Sorama B.V.

export class PromiseExtensions {
    /**
     * Creates a promise that on reject calls a method to determine if the promise should be retried or whether the provided state object should be changed
     * @param promiseAction Action that creates the promise that needs to be retried
     * @param onReject Action to call on rejection of the promise, if the promise no longer needs to be retried, return null
     * @param initialState Initial state object that should be used, can be updated via the onReject method
     */
    public static CreateRetryablePromise<TState, TReturn>(
        promiseAction: (state: TState) => Promise<TReturn>,
        onReject: (attempt: number, state: TState) => TState,
        initialState: TState
    ): Promise<TReturn> {
        return this.CreateRetryablePromiseInternal(promiseAction, onReject, initialState);
    }

    private static CreateRetryablePromiseInternal<TState, TReturn>(
        promiseAction: (state: TState) => Promise<TReturn>,
        onReject: (attempt: number, state: TState) => TState,
        state: TState,
        retryCounter?: number
    ): Promise<TReturn> {
        retryCounter = retryCounter ? retryCounter : 0;

        return promiseAction(state).catch((e) => {
            const newState = onReject(retryCounter, state);
            if (newState === null) {
                throw e;
            }

            if (newState !== state) {
                retryCounter = 0;
            } else {
                retryCounter++;
            }

            return this.CreateRetryablePromiseInternal(promiseAction, onReject, newState, retryCounter);
        });
    }

    public static catchAllErrors<T>(action: () => Promise<T> | T): Promise<T> {
        try {
            return Promise.resolve(action());
        } catch (e) {
            return Promise.reject(e);
        }
    }

    public static allIncludingErrored<TReturn>(promises: Promise<TReturn>[]): Promise<TReturn[]> {
        const errors: unknown[] = [];

        const p: Promise<TReturn>[] = [];
        for (let i = 0; i < promises.length; i++) {
            errors.push(undefined);

            p.push(promises[i].catch((e) => (errors[i] = e)));
        }

        return Promise.all<TReturn>(p).then((result: TReturn[]) => {
            if (errors.some((x) => x !== undefined)) throw errors;

            return result;
        });
    }

    public static delayedPromise<T>(delayInMs: number): Promise<T> {
        return new Promise((resolve, reject) => {
            setTimeout(resolve, delayInMs);
        });
    }

    public static delay<T>(x: Promise<T>, delay: number): Promise<T> {
        return new Promise((resolve, reject) => {
            setTimeout(() => x.then(resolve, reject), delay);
        });
    }
}
